import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { Paper, Typography, Tooltip, IconButton } from '@material-ui/core';
import { Refresh, Delete } from '@material-ui/icons';
import { BackButton } from '../../../sc-utilities';
import MaterialTable from 'material-table';
import { hasPermission, localization, hasAtLeastOnePermission, parseURL, getDimensionsOfPagination } from '../../../sc-utilities';
import AddressDelete from './AddressDelete';
import * as actions from './store/addressActions';
import * as action from '../../../np-core/Auth/store/authActions';
import Toolbar from '../../../sc-utilities/Toolbar'
import { MuiTableRows } from '../../../../themes';
import { CancelToken } from 'axios';

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const AddressList = props => {
    const { classes, translations, permissions, onFetchAddresses, onFetchAddressesCount, fetching, saveColumnsFilter, activeCustomerID, onBulkDeleteAddresses, tableColumns, tableRef } = props;
    const [deleteModalState, setDeleteModalState] = useState({ open: false, addressIDs: [] });
    const searchParams = parseURL(window.location.href)
    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []); const countSource = CancelToken.source();
    const listSource = CancelToken.source();

    const selectionPermissions = hasAtLeastOnePermission(permissions, ['address_delete']);

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: selectionPermissions,
        search: false,
        minBodyHeight: window.innerHeight - 197 < 300 ? 300 : window.innerHeight - 197,
        maxBodyHeight: window.innerHeight - 197,
        pageSizeOptions: [20, 50, 100], loading: true,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize,
        emptyRowsWhenPaging: false
    });

    useEffect(() => {
        return () => {
            countSource.cancel('Operation canceled by the user.');
            listSource.cancel('Operation canceled by the user.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    };

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);

        query['ActiveCustomerID'] = activeCustomerID;
        query['q'] = q;
        let count = await onFetchAddressesCount(query, { countSource: countSource });
        if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
            let dimensions = getDimensionsOfPagination(count.data)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }
        let result = count.data ? await onFetchAddresses(query, { listSource: listSource }) : { data: [] };
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result.data, page: query.page, pageSize: pageSize, totalCount: count.data };
    }

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography className={classes.typography}>{translations['address'] || 'Address'}</Typography>
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" disabled={fetching} onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>
        </div>
    };

    const handleCloseSafe = () => {
        setDeleteModalState({ open: false, addressIDs: [] });
    };

    const handleCloseDanger = async () => {
        await onBulkDeleteAddresses({ AddressIDs: deleteModalState.addressIDs, ActiveCustomerID: activeCustomerID });
        setDeleteModalState({ open: false, addressIDs: [] });
        tableRef.current && tableRef.current.onQueryChange();
    };

    const handleBulkDelete = addressIDs => {
        setDeleteModalState({ open: true, addressIDs: addressIDs });
    };

    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["addresses"] || "Addresses"}`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(address => address.AddressID))
        }
    ];

    const columnHidden = newColumnState => {
        let objToSendColumn = {};
        let objColumns = {};
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden;
            return objColumns;
        })
        objToSendColumn[`Address`] = objColumns;
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID });
        return newColumnState;
    }

    return (
        <Paper>
            <MaterialTable
                components={{
                    Toolbar: props => (
                        <Toolbar data={props} actions={tableActions} selectedRows={props.selectedRows} translations={translations} />
                    )
                }}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                isLoading={fetching}
                localization={localization(translations)}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}

            />
            {hasPermission(permissions, 'address_delete') ? (
                <AddressDelete
                    entityName={translations['addresses'] || 'Addresses'}
                    translations={translations}
                    handleCloseDanger={handleCloseDanger}
                    handleCloseSafe={handleCloseSafe}
                    deleteModalState={deleteModalState}
                />
            ) : null}
        </Paper>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        permissions: state?.core?.login?.permissions || [],
        fetching: state.client.address.fetching
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onFetchAddressesCount: (filter, options) => dispatch(actions.fetchAddressesCount(filter, options)),
        onFetchAddresses: (filter, options) => dispatch(actions.fetchAddresses(filter, options)),
        onBulkDeleteAddresses: address => dispatch(actions.bulkDeleteAddress(address)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(AddressList));